import React, { useEffect, useRef, useState } from "react";
import {
  getEventbriteConnection,
  connectEventbrite,
  disconnectEventbrite,
} from "../services/eventbriteService";
import {
  COPY,
  EVENTBRITE_KEYS_URL,
  NOT_CONNECTED,
  checkToken,
  connectedLine,
  errorOf,
  loadErrorOf,
  normaliseStatus,
  textMaskSupported,
} from "./eventbriteUtils";

// Enter in the token field connects, as a form would, without a form element.
const isEnter = (e) => e.key === "Enter" && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey;

/**
 * Reads the connection once, for the card AND the closed drawer's summary,
 * which is why it lives with the page rather than inside the card (the card
 * only mounts while its drawer is open). Its own request: a server without
 * the endpoint costs this card, never the settings page.
 *
 * State: { loading: true } | { loadError } | { status }.
 */
export const useEventbriteConnection = (onUnauthorized) => {
  const [state, setState] = useState({ loading: true });
  const unauthorized = useRef(onUnauthorized);
  unauthorized.current = onUnauthorized;

  useEffect(() => {
    let alive = true;
    getEventbriteConnection()
      .then((res) => {
        if (alive) setState({ status: normaliseStatus(res && res.data) });
      })
      .catch((err) => {
        if (!alive) return;
        if (err && err.response && err.response.status === 401) {
          if (unauthorized.current) unauthorized.current();
          return;
        }
        setState({ loadError: loadErrorOf(err) });
      });
    return () => {
      alive = false;
    };
  }, []);

  return [state, setState];
};

/**
 * Settings > Integrations > Eventbrite (Events design B, step 1).
 *
 * The founder pastes a Private token; the server checks it with Eventbrite,
 * stores it encrypted, and only ever answers with the account it belongs to
 * and the key's last four characters.
 *
 * 🔴 THE TOKEN IS NEVER REACT STATE. The field is uncontrolled: the token is
 * read from it at Connect, sent once in the PUT body, and the field is
 * emptied once the server says it is stored. Only "is there text" is state.
 * Errors are turned into words here and the error object is never logged,
 * because an axios error carries the request body, token included.
 *
 * 🔴 NOT A PASSWORD FIELD, NOT A FORM. Browsers ignore autocomplete="off" on
 * a password field: after Connect the field leaves the page, which Chrome and
 * Edge read as a finished sign-in and offer to save the token as the admin
 * site's password (synced to the browser account), and a saved sign-in
 * password could be filled into it and sent to Eventbrite. So the field is a
 * text field hidden with CSS (-webkit-text-security) and there is no form element;
 * Enter and the button both connect. A browser without that CSS gets a
 * password field marked new-password instead (saved sign-ins kept out).
 *
 * Only a Super Admin may connect or disconnect: the server refuses everyone
 * else, and says canChange: false in its answer so the field and the
 * Disconnect button are not offered to them at all.
 */
const EventbriteCard = ({ state, onStatus, editable, onUnauthorized }) => {
  const field = useRef(null);
  const [hasText, setHasText] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [masked] = useState(() => textMaskSupported());

  const fail = (err, action) => {
    if (err && err.response && err.response.status === 401 && onUnauthorized) {
      onUnauthorized();
      return;
    }
    setError(errorOf(err, action));
  };

  const connect = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (busy) return;
    const checked = checkToken(field.current ? field.current.value : "");
    if (checked.error) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await connectEventbrite(checked.token);
      const status = normaliseStatus(res && res.data);
      if (!status.connected) {
        const said = res && res.data && typeof res.data.message === "string" ? res.data.message.trim() : "";
        setError(said || COPY.unconfirmed);
        return;
      }
      if (field.current) field.current.value = "";
      setHasText(false);
      onStatus(status);
    } catch (err) {
      fail(err, "connect");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await disconnectEventbrite();
      if (normaliseStatus(res && res.data).connected) {
        setError(COPY.unconfirmed);
        return;
      }
      setConfirming(false);
      onStatus(NOT_CONNECTED);
    } catch (err) {
      fail(err, "disconnect");
    } finally {
      setBusy(false);
    }
  };

  const status = state && state.status;
  const connected = !!(status && status.connected);
  // The Settings screen's own limit AND the server's word for this admin.
  const mayChange = !!editable && !!status && status.canChange !== false;

  return (
    <div className="border radius-8 px-16 py-12" data-testid="eventbrite-card">
      <div className="d-flex align-items-center gap-2 flex-wrap mb-8">
        <span className="fw-semibold">Eventbrite</span>
        {status && (
          <span
            className={
              connected
                ? "bg-success-100 text-success-600 px-8 py-2 radius-4 text-xs fw-semibold"
                : "bg-neutral-200 text-secondary-light px-8 py-2 radius-4 text-xs fw-semibold"
            }
          >
            {connected ? "Connected" : "Not connected"}
          </span>
        )}
      </div>

      {(!state || state.loading) && (
        <div className="text-sm text-secondary-light">Checking the Eventbrite connection…</div>
      )}

      {state && state.loadError && (
        <div className="text-sm text-danger-600" role="alert">
          {state.loadError}
        </div>
      )}

      {status && connected && (
        <>
          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div data-testid="eventbrite-line" style={{ minWidth: 0, flex: 1 }}>
              {connectedLine(status)}
            </div>
            {mayChange && !confirming && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm radius-8 flex-shrink-0"
                onClick={() => {
                  setConfirming(true);
                  setError("");
                }}
              >
                Disconnect
              </button>
            )}
          </div>
          {confirming && (
            <div className="mt-12 px-16 py-12 radius-8 bg-danger-focus" role="group" aria-label="Confirm disconnect">
              <p className="mb-8">{COPY.confirmDisconnect}</p>
              <div className="d-flex gap-2 flex-wrap">
                <button type="button" className="btn btn-danger btn-sm radius-8" disabled={busy} onClick={disconnect}>
                  {busy ? "Disconnecting…" : "Disconnect"}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm radius-8"
                  disabled={busy}
                  onClick={() => {
                    setConfirming(false);
                    setError("");
                  }}
                >
                  Keep it connected
                </button>
              </div>
            </div>
          )}
          {error && (
            <div className="text-sm text-danger-600 mt-8" role="alert">
              {error}
            </div>
          )}
        </>
      )}

      {status && !connected && !mayChange && (
        <div className="text-sm text-secondary-light">{COPY.viewOnly}</div>
      )}

      {status && !connected && mayChange && (
        <div>
          <p className="text-sm text-secondary-light mb-8">
            {COPY.intro}{" "}
            <a href={EVENTBRITE_KEYS_URL} target="_blank" rel="noopener noreferrer">
              Open API keys on Eventbrite
            </a>
          </p>
          <label htmlFor="eventbrite-token" className="form-label mb-4">
            Private token
          </label>
          <div className="d-flex gap-2 flex-wrap align-items-start">
            <input
              ref={field}
              id="eventbrite-token"
              name="eventbrite-private-token"
              type={masked ? "text" : "password"}
              className="form-control"
              style={
                masked
                  ? { maxWidth: 420, flex: "1 1 240px", WebkitTextSecurity: "disc" }
                  : { maxWidth: 420, flex: "1 1 240px" }
              }
              autoComplete={masked ? "off" : "new-password"}
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
              aria-invalid={error ? "true" : "false"}
              aria-describedby={error ? "eventbrite-token-error" : undefined}
              disabled={busy}
              // Like a password field: what is pasted cannot be copied back out.
              onCopy={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (isEnter(e)) connect(e);
              }}
              onChange={(e) => {
                setHasText(e.target.value.trim().length > 0);
                if (error) setError("");
              }}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm radius-8 px-20"
              disabled={busy || !hasText}
              onClick={connect}
            >
              {busy ? "Connecting…" : "Connect"}
            </button>
          </div>
          {error && (
            <div id="eventbrite-token-error" className="text-sm text-danger-600 mt-8" role="alert">
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventbriteCard;
